/* oxlint-disable typescript/no-explicit-any -- route builder with extensive generic type parameters */
import type { Schema } from '@w/schema'
import type { ComponentType } from 'react'
import type {
  AnyRoute,
  RouteTypeInfo,
  RouteChildren,
  RouterContextRecord,
  RouteInstance,
  ResolveFullPath,
  RouteBeforeLoadFn,
  RouteLoaderFn,
  RouteComponentProps,
  RouteErrorComponentProps,
  ParamsFromPath,
  NormalizePath,
  InferSearchResult,
  MergeParams,
  MergeSearch,
} from './types'
import { resolveFullPathRuntime, resolveRouteIdRuntime } from './utils'
import { createRouter as createRouterBase } from './router'
import type { RootTel } from '@w/tel'

type EnsureRootRoute<TRoute extends AnyRoute> =
  TRoute['types']['parentRoute'] extends null ? TRoute : never

type RouteParamsForParent<TRoute extends AnyRoute> =
  TRoute['types']['allParams']

type RouteSearchForParent<TRoute extends AnyRoute> =
  TRoute['types']['fullSearch']

type RouteBaseOptions<
  TParent extends AnyRoute,
  TRouterContext extends RouterContextRecord,
  TPath extends string | undefined,
  TSearchSchema extends Schema | undefined,
  TBeforeLoadResult,
  TLoaderResult,
> = {
  getParentRoute: () => TParent
  path?: TPath
  component?: ComponentType<RouteComponentProps<TRouterContext>>
  /** Component shown while loader is pending. If not set, waits for loader before showing route. */
  pendingComponent?: ComponentType<RouteComponentProps<TRouterContext>>
  /** Component shown when loader throws an error. */
  errorComponent?: ComponentType<RouteErrorComponentProps<TRouterContext>>
  notFoundComponent?: unknown
  searchSchema?: TSearchSchema
  /**
   * Entry-state schema (§5). Declare REQUIRED DEFAULTS — a cold load then
   * yields them, so the read type is always `T` and no consumer has an
   * "absent state" branch to forget.
   */
  stateSchema?: Schema | { parse?: (value: unknown) => unknown }
  beforeLoad?: RouteBeforeLoadFn<
    MergeParams<
      RouteParamsForParent<TParent>,
      ParamsFromPath<NormalizePath<TPath>>
    >,
    MergeSearch<
      RouteSearchForParent<TParent>,
      InferSearchResult<TSearchSchema>
    >,
    TRouterContext,
    TBeforeLoadResult
  >
  loader?: RouteLoaderFn<
    MergeParams<
      RouteParamsForParent<TParent>,
      ParamsFromPath<NormalizePath<TPath>>
    >,
    MergeSearch<
      RouteSearchForParent<TParent>,
      InferSearchResult<TSearchSchema>
    >,
    TRouterContext,
    TLoaderResult
  >
  meta?: Record<string, unknown>
}

type RequiresCustomId<TPath extends string | undefined> = [TPath] extends [
  undefined,
]
  ? true
  : TPath extends '/'
    ? false
    : NormalizePath<TPath> extends ''
      ? true
      : false

type RouteOptions<
  TParent extends AnyRoute,
  TRouterContext extends RouterContextRecord,
  TPath extends string | undefined,
  TSearchSchema extends Schema | undefined,
  TBeforeLoadResult,
  TLoaderResult,
  TId extends string,
> = RouteBaseOptions<
  TParent,
  TRouterContext,
  TPath,
  TSearchSchema,
  TBeforeLoadResult,
  TLoaderResult
> &
  (RequiresCustomId<TPath> extends true ? { id: TId } : { id?: TId })

type RootRouteOptions<
  TRouterContext extends RouterContextRecord,
  TBeforeLoadResult,
  TLoaderResult,
  TId extends string,
> = {
  id?: TId
  notFoundComponent?: unknown
  pendingComponent?: ComponentType<RouteComponentProps<TRouterContext>>
  errorComponent?: ComponentType<RouteErrorComponentProps<TRouterContext>>
  beforeLoad?: RouteBeforeLoadFn<{}, {}, TRouterContext, TBeforeLoadResult>
  loader?: RouteLoaderFn<{}, {}, TRouterContext, TLoaderResult>
  meta?: Record<string, unknown>
}

export const createRouteInstance = <
  TType extends RouteTypeInfo<
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  >,
>(
  typeInfo: TType,
): RouteInstance<TType> => {
  const route: RouteInstance<TType> = {
    id: typeInfo.id,
    path: typeInfo.path,
    fullPath: typeInfo.fullPath,
    to: typeInfo.fullPath,
    parentRoute: typeInfo.parentRoute,
    types: typeInfo,
    options: typeInfo.options,
    children: typeInfo.children,
    getParentRoute: () => typeInfo.parentRoute,
    addChildren<TChildren extends RouteChildren>(children: TChildren) {
      const nextTypeInfo: RouteTypeInfo<
        TType['id'],
        TType['path'],
        TType['fullPath'],
        TType['parentRoute'],
        TType['params'],
        TType['allParams'],
        TType['search'],
        TType['fullSearch'],
        TType['loaderData'],
        TType['beforeLoadData'],
        TType['allContext'],
        TChildren
      > = {
        ...typeInfo,
        children,
      }
      return createRouteInstance(nextTypeInfo)
    },
  }
  return route
}

export function bindCreateRouteWithContext<
  TRouterContext extends RouterContextRecord = {},
>() {
  const createRootRoute = <
    TBeforeLoadResult = unknown,
    TLoaderResult = unknown,
    TId extends string = '__root__',
  >(
    options: RootRouteOptions<
      TRouterContext,
      TBeforeLoadResult,
      TLoaderResult,
      TId
    > = {},
  ) => {
    const typeInfo = {
      id: (options.id ?? '__root__') as TId,
      path: undefined,
      fullPath: '/' as const,
      params: {} as {},
      allParams: {} as {},
      search: {} as {},
      fullSearch: {} as {},
      loaderData: undefined as Awaited<TLoaderResult>,
      beforeLoadData: undefined as Awaited<TBeforeLoadResult>,
      parentRoute: null,
      allContext: {} as TRouterContext,
      children: [] as const,
      options,
    } as RouteTypeInfo<
      TId,
      undefined,
      '/',
      null,
      {},
      {},
      {},
      {},
      Awaited<TLoaderResult>,
      Awaited<TBeforeLoadResult>,
      TRouterContext,
      readonly []
    >
    return createRouteInstance(typeInfo)
  }

  const createRoute = <
    TParent extends AnyRoute,
    const TPath extends string | undefined = undefined,
    TSearchSchema extends Schema | undefined = undefined,
    TBeforeLoadResult = unknown,
    TLoaderResult = unknown,
    TId extends string = string extends NormalizePath<TPath>
      ? string
      : NormalizePath<TPath> extends ''
        ? '__index__'
        : NormalizePath<TPath> extends string
          ? NormalizePath<TPath>
          : string,
  >(
    options: RouteOptions<
      TParent,
      TRouterContext,
      TPath,
      TSearchSchema,
      TBeforeLoadResult,
      TLoaderResult,
      TId
    >,
  ) => {
    const parent = options.getParentRoute()
    type LocalPath = NormalizePath<TPath>
    type LocalParams = ParamsFromPath<LocalPath>
    type AllParams = MergeParams<RouteParamsForParent<TParent>, LocalParams>
    type LocalSearch = InferSearchResult<TSearchSchema>
    type AllSearch = MergeSearch<RouteSearchForParent<TParent>, LocalSearch>
    const fullPath = resolveFullPathRuntime(
      parent.fullPath,
      options.path,
    ) as ResolveFullPath<TParent['types']['fullPath'], TPath>
    const id = resolveRouteIdRuntime(options.path, options.id) as TId
    const typeInfo = {
      id,
      path: options.path ?? undefined,
      fullPath,
      params: {} as LocalParams,
      allParams: {} as AllParams,
      search: {} as LocalSearch,
      fullSearch: {} as AllSearch,
      loaderData: undefined as Awaited<TLoaderResult>,
      beforeLoadData: undefined as Awaited<TBeforeLoadResult>,
      parentRoute: parent,
      allContext: parent.types.allContext,
      children: [] as const,
      options,
    } as RouteTypeInfo<
      TId,
      TPath,
      ResolveFullPath<TParent['types']['fullPath'], TPath>,
      TParent,
      LocalParams,
      AllParams,
      LocalSearch,
      AllSearch,
      Awaited<TLoaderResult>,
      Awaited<TBeforeLoadResult>,
      TParent['types']['allContext'],
      readonly []
    >
    return createRouteInstance(typeInfo)
  }

  const createRouter = <TRouteTree extends AnyRoute>(options: {
    routeTree: EnsureRootRoute<TRouteTree>
    ctx: TRouterContext
    tel?: RootTel
  }) => {
    return createRouterBase(options)
  }

  return {
    createRootRoute,
    createRoute,
    createRouter,
  }
}
